import {DomainStore} from 'shared/store';
import {observable, action} from 'mobx';
import moment from 'moment';
import {v4 as uuidv4} from 'uuid';
import {t} from 'shared/core';
import {auth, endpoints, types} from 'main/tools';
import {InterviewSchedule} from 'main/stores/interview_schedules';
import User from 'main/stores/users/User';
import _ from 'lodash';

function defaultBusinessHourWindows() {
  return [0, 1, 2, 3, 4, 5, 6].map(day => ({
    id: uuidv4(),
    dayOfWeek: day,
    startTime: [1, 2, 3, 4, 5].includes(day) ? moment().hour(9).minute(0).format(t('components.dates.TIMESTAMP_FORMAT')) : '',
    endTime: [1, 2, 3, 4, 5].includes(day) ? moment().hour(17).minute(0).format(t('components.dates.TIMESTAMP_FORMAT')) : ''
  }));
}

function withBlankDays(windows) {
  if (_.isEmpty(windows)) return defaultBusinessHourWindows();

  const result = windows.map(window => ({
    ...window,
    startTime: moment(window.startTime).utc().format(t('components.dates.TIMESTAMP_FORMAT')),
    endTime: moment(window.endTime).utc().format(t('components.dates.TIMESTAMP_FORMAT'))
  }));

  [0, 1, 2, 3, 4, 5, 6].forEach(day => {
    if (!result.some(w => w.dayOfWeek === day)) {
      result.push({id: uuidv4(), dayOfWeek: day, startTime: '', endTime: ''});
    }
  });

  return result;
}

const STEP_CONFIG = {
  interview_details: {
    fields: ['calendarEventName', 'templateName', 'eventDescription', 'duration', 'location'],
    endpoint: endpoints.INTERVIEW_SCHEDULE.UPDATE_DETAILS
  },
  interviewers: {
    fields: ['eventHost', 'interviewers'],
    endpoint: endpoints.INTERVIEW_SCHEDULE.UPDATE_INTERVIEWERS
  },
  availability: {
    fields: [
      'relativeEndDateUnit', 'relativeEndDateValue',
      'minimumNoticeValue', 'minimumNoticeUnit',
      'eventBuffer', 'availabilityWindows'
    ],
    endpoint: endpoints.INTERVIEW_SCHEDULE.UPDATE_AVAILABILITY
  },
  invitation_email: {
    fields: ['emailSubject', 'emailRichText', 'emailLexicalState'],
    endpoint: endpoints.INTERVIEW_SCHEDULE.UPDATE_EMAIL
  },
  review: {
    fields: [],
    endpoint: endpoints.INTERVIEW_SCHEDULE.CONFIGURE
  }
};

class InterviewScheduleEditFlowState {
  store = new DomainStore();
  history;
  match;

  @observable interviewSchedule = {};
  @observable users = [];
  @observable errors = {};

  receiveProps({history, match}) {
    this.history = history;
    this.match = match;
  }

  @action async load() {
    await this.store._compose([
      endpoints.INTERVIEW_SCHEDULE.with(this.match.params.id),
      endpoints.USERS.ACTIVE
    ]);

    this.interviewSchedule = new InterviewSchedule(this.store._getSingle(types.INTERVIEW_SCHEDULE));

    this.users = this.store._getAll(types.USER, User);
  }

  @action async onStepChanged({location}) {
    switch (location) {
      case 'interviewers': {
        if (_.isEmpty(this.interviewSchedule.interviewers)) {
          this.interviewSchedule.addInterviewer(_.find(this.users, {id: auth.user.id}));
        }
        if (!this.interviewSchedule.eventHost) {
          this.interviewSchedule.merge({eventHost: _.find(this.users, {id: auth.user.id})});
        }
        break;
      }
      case 'availability':
        if (this.interviewSchedule.relativeEndDateValue == null) {
          this.interviewSchedule.merge({
            relativeEndDateValue: 1,
            relativeEndDateUnit: 'weeks'
          });
        }
        if (this.interviewSchedule.minimumNoticeUnit == null) {
          this.interviewSchedule.merge({minimumNoticeUnit: 'hours'});
        }
        this.interviewSchedule.merge({
          availabilityWindows: withBlankDays(this.interviewSchedule.availabilityWindows)
        });
        break;
      default:
        break;
    }
  }

  @action async onStepSubmitted({location}) {
    const submitted = await this.submitStep(location);

    if (submitted && location === 'review') {
      this.history.push('/interview-schedules');
    }

    return submitted;
  }

  @action async submitStep(location) {
    const {fields, endpoint} = STEP_CONFIG[location];

    const {model, errors} = await this.store.patch(
      endpoint.with(this.match.params.id),
      types.INTERVIEW_SCHEDULE,
      this.interviewSchedule.pick(fields)
    );

    this.errors = errors;
    if (!model) return false;

    this.interviewSchedule.update(model);
    return true;
  }
}

export default InterviewScheduleEditFlowState;
